/**
 * InfinitSales - Conta do Instagram conectada (ver/desconectar)
 *
 * Espelho do estado "ja conectado" do ConnectionModal do WhatsApp Business,
 * para o canal Instagram: mostra qual conta esta ativa e permite desconectar.
 * O fluxo de CONECTAR (token do painel da Meta) vive no
 * InstagramConnectionModal, dentro do inbox do IG — quando nao ha conta, este
 * modal so encaminha para la via onRequestConnect.
 *
 * Desconectar tem duas forcas, espelhando a API (DELETE /account?purge=):
 * sem purge a conta apenas para de operar e o historico volta ao reconectar;
 * com purge as conversas sao apagadas de vez — por isso o checkbox explicito.
 */
import React, { useState, useEffect } from 'react';
import {
  X,
  Instagram,
  Loader2,
  AlertTriangle,
  LogOut,
  Wifi,
  AtSign,
  User,
  CalendarClock,
  Plug,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import instagramService from '../../services/instagram/instagramService';

const IG_PINK = '#c13584';

const InstagramAccountModal = ({ onClose, onDisconnected, onRequestConnect }) => {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  // Espelho (Instagram da equipe): membro operando a conta do ADMIN — o modal
  // vira somente-leitura (desconectar e acao do dono da conexao).
  const [mirror, setMirror] = useState(false);
  const [account, setAccount] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [purge, setPurge] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await instagramService.getStatus();
        if (cancelled) return;
        const data = status?.data || {};
        setConnected(!!data.connected);
        setMirror(!!data.mirror);
        if (data.connected) {
          // O /account traz o doc completo (conectada em, validade do token);
          // se falhar, o proprio /status ja tem o essencial (@username, nome).
          try {
            const acc = await instagramService.getAccount();
            if (!cancelled) setAccount(acc?.data || data);
          } catch {
            if (!cancelled) setAccount(data);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setConnected(false);
          if (e?.response?.status !== 404) {
            setError('Não foi possível consultar o status da conexão.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await instagramService.disconnect(purge);
      onDisconnected?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'object' ? detail?.message : detail)
        || 'Erro ao desconectar a conta.'
      );
      setShowConfirm(false);
      setDisconnecting(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('pt-BR');
  };

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    },
    modal: {
      backgroundColor: colors.bgPrimary, borderRadius: 16, maxWidth: 440,
      width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    closeBtn: {
      position: 'absolute', top: 16, right: 16, width: 36, height: 36,
      border: 'none', background: colors.bgTertiary, color: colors.textSecondary,
      cursor: 'pointer', borderRadius: '50%', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 10,
    },
    content: { padding: '40px 32px' },
    container: { textAlign: 'center' },
    iconBox: {
      width: 80, height: 80, backgroundColor: `${IG_PINK}15`, borderRadius: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px', position: 'relative',
    },
    connectedBadge: {
      position: 'absolute', bottom: -4, right: -4, width: 28, height: 28,
      backgroundColor: IG_PINK, borderRadius: '50%', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      border: `3px solid ${colors.bgPrimary}`,
    },
    title: {
      fontSize: 22, fontWeight: 600, color: colors.textPrimary, marginBottom: 8,
    },
    subtitle: {
      fontSize: 14, color: colors.textSecondary, marginBottom: 24,
    },
    accountCard: {
      backgroundColor: colors.bgSecondary, borderRadius: 12, padding: 20,
      marginBottom: 24, textAlign: 'left',
    },
    accountRow: {
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
    },
    accountRowLast: {
      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0,
    },
    accountIcon: {
      width: 36, height: 36, borderRadius: 8, backgroundColor: colors.bgTertiary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: colors.textSecondary, overflow: 'hidden', flexShrink: 0,
    },
    accountLabel: { fontSize: 12, color: colors.textTertiary, marginBottom: 2 },
    accountValue: {
      fontSize: 15, fontWeight: 500, color: colors.textPrimary,
      overflowWrap: 'anywhere',
    },
    errorBox: {
      backgroundColor: `${colors.danger || '#ef4444'}15`,
      border: `1px solid ${colors.danger || '#ef4444'}30`,
      borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex',
      alignItems: 'flex-start', gap: 12, textAlign: 'left',
    },
    errorText: {
      fontSize: 14, color: colors.danger || '#ef4444', margin: 0, flex: 1,
      lineHeight: 1.4,
    },
    disconnectBtn: {
      width: '100%', padding: '14px 24px', backgroundColor: 'transparent',
      border: `1px solid ${colors.danger || '#ef4444'}`,
      color: colors.danger || '#ef4444', borderRadius: 12, fontSize: 15,
      fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 10, transition: 'all 0.2s',
    },
    connectActionBtn: {
      width: '100%', padding: '14px 24px', backgroundColor: IG_PINK,
      border: 'none', color: '#fff', borderRadius: 12, fontSize: 15,
      fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 10,
    },
    footerNote: {
      marginTop: 20, fontSize: 12, color: colors.textTertiary,
    },
    confirmOverlay: {
      position: 'absolute', inset: 0, backgroundColor: colors.bgPrimary,
      borderRadius: 16, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 36, zIndex: 10,
    },
    confirmIcon: {
      width: 64, height: 64,
      backgroundColor: `${colors.danger || '#ef4444'}15`, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 20,
    },
    confirmTitle: {
      fontSize: 20, fontWeight: 600, color: colors.textPrimary, marginBottom: 8,
      textAlign: 'center',
    },
    confirmText: {
      fontSize: 14, color: colors.textSecondary, textAlign: 'center',
      marginBottom: 20, lineHeight: 1.5,
    },
    purgeRow: {
      display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 24,
      padding: '12px 14px', borderRadius: 10,
      backgroundColor: colors.bgSecondary, cursor: 'pointer',
      fontSize: 13, color: colors.textPrimary, lineHeight: 1.4, textAlign: 'left',
    },
    confirmButtons: { display: 'flex', gap: 12, width: '100%' },
    confirmCancelBtn: {
      flex: 1, padding: '12px 20px', backgroundColor: colors.bgTertiary,
      border: 'none', color: colors.textPrimary, borderRadius: 10, fontSize: 14,
      fontWeight: 500, cursor: 'pointer',
    },
    confirmDisconnectBtn: {
      flex: 1, padding: '12px 20px',
      backgroundColor: colors.danger || '#ef4444', border: 'none',
      color: 'white', borderRadius: 10, fontSize: 14, fontWeight: 500,
      cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 8,
    },
    loadingBox: {
      padding: '60px 0', display: 'flex', alignItems: 'center',
      justifyContent: 'center',
    },
  };

  const tokenExpiry = formatDate(account?.token_expires_at);
  const connectedAt = formatDate(account?.connected_at || account?.created_at);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeBtn} onClick={onClose}>
          <X size={20} />
        </button>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loadingBox}>
              <Loader2
                size={32}
                color={IG_PINK}
                style={{ animation: 'spin 1s linear infinite' }}
              />
            </div>
          ) : connected ? (
            <div style={styles.container}>
              <div style={styles.iconBox}>
                <Instagram size={40} color={IG_PINK} />
                <div style={styles.connectedBadge}>
                  <Wifi size={14} color="white" />
                </div>
              </div>
              <h2 style={styles.title}>Instagram Conectado</h2>
              <p style={styles.subtitle}>
                {mirror
                  ? 'Conta da empresa, conectada pelo administrador. Você atende as conversas atribuídas a você.'
                  : 'Sua conta está ativa e recebendo mensagens'}
              </p>

              <div style={styles.accountCard}>
                <div style={styles.accountRow}>
                  <div style={styles.accountIcon}>
                    {account?.profile_picture_url ? (
                      <img
                        src={account.profile_picture_url}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <AtSign size={18} />
                    )}
                  </div>
                  <div>
                    <div style={styles.accountLabel}>Conta</div>
                    <div style={styles.accountValue}>
                      {account?.username ? `@${account.username}` : account?.ig_user_id}
                    </div>
                  </div>
                </div>

                {account?.name && (
                  <div style={styles.accountRow}>
                    <div style={styles.accountIcon}>
                      <User size={18} />
                    </div>
                    <div>
                      <div style={styles.accountLabel}>Nome</div>
                      <div style={styles.accountValue}>{account.name}</div>
                    </div>
                  </div>
                )}

                {(tokenExpiry || connectedAt) && (
                  <div style={styles.accountRowLast}>
                    <div style={styles.accountIcon}>
                      <CalendarClock size={18} />
                    </div>
                    <div>
                      <div style={styles.accountLabel}>
                        {tokenExpiry ? 'Token válido até' : 'Conectada em'}
                      </div>
                      <div style={styles.accountValue}>
                        {tokenExpiry || connectedAt}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div style={styles.errorBox}>
                  <AlertTriangle
                    size={18}
                    color={colors.danger || '#ef4444'}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <p style={styles.errorText}>{error}</p>
                </div>
              )}

              {!mirror && (
                <>
                  <button
                    style={styles.disconnectBtn}
                    onClick={() => setShowConfirm(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.danger || '#ef4444'}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <LogOut size={18} />
                    Desconectar conta
                  </button>

                  <p style={styles.footerNote}>
                    Ao desconectar, as mensagens do Direct param de chegar nesta
                    plataforma.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div style={styles.container}>
              <div style={styles.iconBox}>
                <Instagram size={40} color={IG_PINK} />
              </div>
              <h2 style={styles.title}>Instagram não conectado</h2>
              <p style={styles.subtitle}>
                Conecte uma conta para receber as mensagens do Direct aqui
              </p>

              {error && (
                <div style={styles.errorBox}>
                  <AlertTriangle
                    size={18}
                    color={colors.danger || '#ef4444'}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <p style={styles.errorText}>{error}</p>
                </div>
              )}

              <button style={styles.connectActionBtn} onClick={onRequestConnect}>
                <Plug size={16} />
                Conectar Instagram
              </button>
            </div>
          )}

          {showConfirm && (
            <div style={styles.confirmOverlay}>
              <div style={styles.confirmIcon}>
                <AlertTriangle size={32} color={colors.danger || '#ef4444'} />
              </div>
              <h3 style={styles.confirmTitle}>Desconectar Instagram?</h3>
              <p style={styles.confirmText}>
                {account?.username ? `A conta @${account.username}` : 'A conta'}
                {' '}vai parar de enviar e receber mensagens pela plataforma.
                Sem apagar o histórico, as conversas voltam quando ela
                reconectar.
              </p>

              <label style={styles.purgeRow}>
                <input
                  type="checkbox"
                  checked={purge}
                  onChange={(e) => setPurge(e.target.checked)}
                  disabled={disconnecting}
                  style={{ marginTop: 2, accentColor: colors.danger || '#ef4444' }}
                />
                <span>Apagar também as conversas e mensagens (irreversível)</span>
              </label>

              <div style={styles.confirmButtons}>
                <button
                  style={styles.confirmCancelBtn}
                  onClick={() => setShowConfirm(false)}
                  disabled={disconnecting}
                >
                  Cancelar
                </button>
                <button
                  style={{
                    ...styles.confirmDisconnectBtn,
                    ...(disconnecting ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                  }}
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Desconectando...
                    </>
                  ) : (
                    <>
                      <LogOut size={16} />
                      Desconectar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default InstagramAccountModal;
